import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { toast } from 'sonner';

// Add / edit a PM Package: name + parts list (searchable from inventory) with
// quantities. pkg=null → create mode.
const PMPackageDialog = ({ open, onClose, machineryId, pkg, onSaved }) => {
  const [name, setName] = useState('');
  const [parts, setParts] = useState([]);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [saving, setSaving] = useState(false);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (open) {
      setName(pkg?.name || '');
      setParts((pkg?.parts || []).map((p) => ({ part_number: p.part_number, qty: p.qty, name: p.name })));
      setQuery('');
      setResults([]);
    }
  }, [open, pkg]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!query.trim()) { setResults([]); return; }
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await axios.get(`${API}/inventory/search`, { params: { q: query.trim() } });
        setResults((res.data || []).slice(0, 8));
      } catch { setResults([]); }
    }, 250);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [query]);

  const addPart = (partNumber, partName) => {
    if (parts.some((p) => p.part_number.toLowerCase() === partNumber.toLowerCase())) {
      toast.error('Part is already in the list');
      return;
    }
    setParts((prev) => [...prev, { part_number: partNumber, qty: 1, name: partName }]);
    setQuery('');
    setResults([]);
  };

  const handleSave = async () => {
    if (!name.trim()) { toast.error('Package name is required'); return; }
    setSaving(true);
    const payload = {
      name: name.trim(),
      parts: parts.map((p) => ({ part_number: p.part_number, qty: Math.max(1, parseInt(p.qty, 10) || 1) })),
    };
    try {
      if (pkg) {
        await axios.put(`${API}/pm-packages/${pkg.id}`, payload);
        toast.success('PM package updated');
      } else {
        await axios.post(`${API}/machinery/${machineryId}/pm-packages`, payload);
        toast.success('PM package created');
      }
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save package');
    } finally {
      setSaving(false);
    }
  };

  const exactMatch = results.some((r) => r.part_number.toLowerCase() === query.trim().toLowerCase());

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="pm-package-dialog">
        <DialogHeader>
          <DialogTitle>{pkg ? 'Edit PM Package' : 'New PM Package'}</DialogTitle>
          <DialogDescription>
            Named parts kit checked against live inventory. Parts are still consumed via the work order on completion.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="pm-pkg-name">Package Name</Label>
            <Input
              id="pm-pkg-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder='e.g. "8000hr Service", "16000hr / 24mo Service"'
              data-testid="pm-package-name-input"
            />
          </div>

          <div className="space-y-2">
            <Label>Parts &amp; Quantities Needed</Label>
            {parts.length === 0 && (
              <div className="text-sm text-slate-500 italic">No parts yet — search below to add.</div>
            )}
            {parts.map((p, idx) => (
              <div key={p.part_number} className="flex items-center gap-2 border rounded-md px-2 py-1.5" data-testid={`pm-package-part-row-${idx}`}>
                <div className="flex-1 min-w-0">
                  <div className="font-mono text-sm font-semibold truncate">{p.part_number}</div>
                  {p.name && <div className="text-xs text-slate-500 truncate">{p.name}</div>}
                </div>
                <Input
                  type="number"
                  min="1"
                  className="w-20 h-8 text-sm"
                  value={p.qty}
                  onChange={(e) => setParts((prev) => prev.map((x, i) => (i === idx ? { ...x, qty: e.target.value } : x)))}
                  data-testid={`pm-package-part-qty-${idx}`}
                />
                <button
                  type="button"
                  className="text-red-500 hover:text-red-700 px-1"
                  onClick={() => setParts((prev) => prev.filter((_, i) => i !== idx))}
                  title="Remove part"
                  data-testid={`pm-package-part-remove-${idx}`}
                >
                  ✕
                </button>
              </div>
            ))}
          </div>

          <div className="space-y-1 relative">
            <Label htmlFor="pm-pkg-search">Add Part (search inventory)</Label>
            <Input
              id="pm-pkg-search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by part number or name…"
              data-testid="pm-package-part-search"
            />
            {(results.length > 0 || (query.trim() && !exactMatch)) && (
              <div className="border rounded-md divide-y bg-white shadow-sm max-h-48 overflow-y-auto">
                {results.map((r) => (
                  <button
                    key={r.id}
                    type="button"
                    className="w-full text-left px-3 py-2 hover:bg-slate-50 text-sm"
                    onClick={() => addPart(r.part_number, r.name)}
                    data-testid={`pm-package-search-result-${r.id}`}
                  >
                    <span className="font-mono font-semibold">{r.part_number}</span>
                    <span className="text-slate-500 ml-2">{r.name}</span>
                    <span className="text-xs text-slate-400 ml-2">({r.quantity} {r.unit} on hand)</span>
                  </button>
                ))}
                {query.trim() && !exactMatch && (
                  <button
                    type="button"
                    className="w-full text-left px-3 py-2 hover:bg-slate-50 text-sm text-slate-600"
                    onClick={() => addPart(query.trim(), null)}
                    data-testid="pm-package-add-raw-part"
                  >
                    + Add &quot;{query.trim()}&quot; as a part number (not in inventory — will show unavailable)
                  </button>
                )}
              </div>
            )}
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={onClose} data-testid="pm-package-cancel-btn">Cancel</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="pm-package-save-btn">
              {saving ? 'Saving…' : pkg ? 'Save Changes' : 'Create Package'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PMPackageDialog;
